import React from 'react';
import { WhatsAppButton, WhatsAppButtonType } from '../../types';
import { Tabs, btn, btnTone, input, label } from '../../components/Kit';

interface ButtonEditorProps {
  buttonType: 'quick_reply' | 'cta' | null;
  buttons: WhatsAppButton[];
  onChange: (buttons: WhatsAppButton[]) => void;
  onButtonTypeChange: (type: 'quick_reply' | 'cta' | null) => void;
  maxButtons?: number;
  showTypeToggle?: boolean;
}

const ButtonEditor: React.FC<ButtonEditorProps> = ({
  buttonType,
  buttons,
  onChange,
  onButtonTypeChange,
  maxButtons = 3,
  showTypeToggle = true,
}) => {
  const handleAddButton = () => {
    if (buttonType === 'quick_reply' && buttons.length < maxButtons) {
      onChange([...buttons, { type: 'QUICK_REPLY', text: '' }]);
    } else if (buttonType === 'cta' && buttons.length < 2) {
      onChange([...buttons, { type: 'PHONE_NUMBER', text: '' }]);
    }
  };

  const handleRemoveButton = (index: number) => {
    const newButtons = buttons.filter((_, i) => i !== index);
    onChange(newButtons);
  };

  const handleButtonChange = (index: number, field: keyof WhatsAppButton, value: string) => {
    const newButtons = [...buttons];
    newButtons[index] = { ...newButtons[index], [field]: value };

    // If changing CTA type, reset irrelevant fields
    if (field === 'type') {
      if (value === 'PHONE_NUMBER') {
        newButtons[index].url = undefined;
        newButtons[index].flow_id = undefined;
        newButtons[index].flow_action = undefined;
        newButtons[index].navigate_screen = undefined;
      } else if (value === 'URL') {
        newButtons[index].phone_number = undefined;
        newButtons[index].flow_id = undefined;
        newButtons[index].flow_action = undefined;
        newButtons[index].navigate_screen = undefined;
      } else if (value === 'FLOW') {
        newButtons[index].phone_number = undefined;
        newButtons[index].url = undefined;
        newButtons[index].flow_action = 'navigate';
      }
    }

    onChange(newButtons);
  };

  const handleTypeToggle = (type: 'quick_reply' | 'cta' | null) => {
    if (type === buttonType) {
      // If clicking same type, deselect (set to null)
      onButtonTypeChange(null);
      onChange([]);
    } else {
      onButtonTypeChange(type);
      // Initialize with one button of the new type
      if (type === 'quick_reply') {
        onChange([{ type: 'QUICK_REPLY', text: '' }]);
      } else if (type === 'cta') {
        onChange([{ type: 'PHONE_NUMBER', text: '' }]);
      } else {
        onChange([]);
      }
    }
  };

  const typeTabs: ['quick_reply' | 'cta' | 'none', string][] = [['quick_reply', 'Quick replies'], ['cta', 'Call to action'], ['none', 'None']];

  return (
    <div className="space-y-3">
      {showTypeToggle && (
        <div className="flex items-center gap-3 flex-wrap">
          <Tabs value={buttonType ?? 'none'} items={typeTabs}
                onChange={(k) => { const t = k === 'none' ? null : k; if (t !== buttonType) handleTypeToggle(t); }} />
          {buttonType && (
            <span className="text-[12px] text-graphite">up to {buttonType === 'cta' ? 2 : maxButtons}</span>
          )}
        </div>
      )}

      {buttonType === 'quick_reply' && (
        <div>
          {buttons.map((button, index) => (
            <div key={index} className="flex gap-2 items-center py-1.5">
              <span className="w-5 text-[12px] text-graphite num text-right">{index + 1}</span>
              <input
                type="text"
                value={button.text}
                onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                maxLength={25}
                placeholder={`Button ${index + 1} label`}
                className={`${input} flex-1`}
              />
              <span className="text-graphite text-[11px] w-10 text-right num">{button.text.length}/25</span>
              <RemoveButton onClick={() => handleRemoveButton(index)} />
            </div>
          ))}
          {buttons.length < maxButtons && (
            <button type="button" onClick={handleAddButton} className={`${btn} ${btnTone.ghost} mt-1 ml-5`}>+ Add button</button>
          )}
        </div>
      )}

      {buttonType === 'cta' && (
        <div>
          {buttons.map((button, index) => (
            <div key={index} className="flex gap-2 items-end py-2 border-b border-charcoal/[0.06] last:border-b-0">
              <span className="w-5 h-9 leading-9 text-[12px] text-graphite num text-right">{index + 1}</span>
              <div className="w-[150px] flex-none">
                <label className={label}>Type</label>
                <select
                  value={button.type}
                  onChange={(e) => handleButtonChange(index, 'type', e.target.value as WhatsAppButtonType)}
                  className={`${input} w-full`}
                >
                  <option value="PHONE_NUMBER">Phone number</option>
                  <option value="URL">URL</option>
                  <option value="FLOW">WhatsApp Flow</option>
                </select>
              </div>
              <div className="flex-1 min-w-0">
                <label className={label}>Label <span className="normal-case tracking-normal font-normal num">{button.text.length}/25</span></label>
                <input
                  type="text"
                  value={button.text}
                  onChange={(e) => handleButtonChange(index, 'text', e.target.value)}
                  maxLength={25}
                  placeholder="e.g. Call us"
                  className={`${input} w-full`}
                />
              </div>
              <div className="flex-1 min-w-0">
                <label className={label}>{button.type === 'PHONE_NUMBER' ? 'Phone number' : 'URL'}</label>
                <input
                  type={button.type === 'URL' ? 'url' : 'tel'}
                  value={button.type === 'PHONE_NUMBER' ? (button.phone_number || '') : (button.url || '')}
                  onChange={(e) => handleButtonChange(
                    index,
                    button.type === 'PHONE_NUMBER' ? 'phone_number' : 'url',
                    e.target.value
                  )}
                  placeholder={button.type === 'PHONE_NUMBER' ? '+91XXXXXXXXXX' : 'https://example.com'}
                  className={`${input} w-full`}
                />
              </div>
              <RemoveButton onClick={() => handleRemoveButton(index)} />
            </div>
          ))}
          {buttons.length < 2 && (
            <button type="button" onClick={handleAddButton} className={`${btn} ${btnTone.ghost} mt-1 ml-5`}>+ Add button</button>
          )}
        </div>
      )}

      {buttonType === null && showTypeToggle && (
        <p className="text-graphite text-[12.5px]">No buttons.</p>
      )}
    </div>
  );
};

const RemoveButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" onClick={onClick} aria-label="Remove button"
          className="w-9 h-9 flex-none grid place-items-center rounded-lg text-graphite hover:text-brick hover:bg-brick/5 transition-colors">✕</button>
);

export default ButtonEditor;
